import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Table, 
  Spin, 
  Empty, 
  Row, 
  Col, 
  Typography, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Pagination, 
  Select, 
  Modal, 
  Form,
  Switch,
  Popconfirm
} from 'antd'
import { ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import customerService from '../services/customerService'
import { Customer, CustomerFilter } from '../types/customers'
import debounce from 'lodash/debounce'
import { useSnackbar } from 'notistack'

const { Title } = Typography
const { Search, TextArea } = Input
const { Option } = Select

const Customers: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [filter, setFilter] = useState<CustomerFilter>({ page: 1, limit: 100 })
  const [total, setTotal] = useState<number>(0)
  const sourceOptions = [
    { value: undefined, label: 'Tất cả nguồn' },
    { value: 'website', label: 'Website' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'contact_form', label: 'Form liên hệ' },
    { value: 'social_media', label: 'Mạng xã hội' },
    { value: 'referral', label: 'Giới thiệu' },
    { value: 'other', label: 'Khác' },
  ]

  // Debounce search
  const debouncedSearch = debounce((value: string) => {
    setFilter(prev => ({ ...prev, search: value.trim(), page: 1 }))
  }, 800)

  const handleSearch = (value: string) => {
    debouncedSearch(value)
  }

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoading(true)
      try {
        const result = await customerService.getCustomers(filter)
        setCustomers(result.customers)
        setTotal(result.total)
      } catch (error) {
        setCustomers([])
        setTotal(0)
      } finally {
        setIsLoading(false)
      }
    }
    fetchCustomers()
  }, [filter])

  const handleRefresh = () => {
    setFilter({ ...filter })
  }

  const handlePageChange = (page: number, pageSize?: number) => {
    setFilter(prev => ({ ...prev, page, limit: pageSize || prev.limit }))
  }

  const handleSourceChange = (value: string | undefined) => {
    setFilter(prev => ({ ...prev, source: value, page: 1 }))
  }

  // Thêm state cho modal tạo mới
  const [showModal, setShowModal] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [form] = Form.useForm()
  
  // State cho modal edit
  const [showEditModal, setShowEditModal] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(null)
  const [editForm] = Form.useForm()

  const handleOpenModal = () => {
    setShowModal(true)
    form.resetFields()  
  }
  const handleCloseModal = () => {
    setShowModal(false)
    form.resetFields()
  }

  const handleCreateCustomer = async () => {
    try {
      console.log('form', form.getFieldsValue())
      const values = await form.validateFields()
      console.log('values', values)
      setCreateLoading(true)
      await customerService.createCustomer({
        email: values.email,
        fullName: values.fullName,
        phone: values.phone,
        company: values.company,
        position: values.position,
        source: values.source,
        interests: values.interests || [],
        notes: values.notes,
        tags: values.tags || []
      })
      enqueueSnackbar('Tạo khách hàng thành công!', { variant: 'success' })
      handleCloseModal()
      setFilter({ ...filter }) // refetch
    } catch (err: any) {
      enqueueSnackbar('Tạo khách hàng thất bại!', { variant: 'error' })
      console.log('err createCustomer', err)
    } finally {
      setCreateLoading(false)
    }
  }

  const handleEdit = (customer: Customer) => {
    setCurrentCustomer(customer)
    setShowEditModal(true)
    // Fill form với dữ liệu khách hàng
    editForm.setFieldsValue({
      fullName: customer.fullName,
      email: customer.email,
      phone: customer.phone,
      source: (customer as any).source,
      interests: (customer as any).interests || [],
      notes: (customer as any).notes,
      tags: (customer as any).tags || [],
      isActive: customer.isActive ?? true
    })
  }

  const handleEditModalOk = async () => {
    try {
      const values = await editForm.validateFields()
      setEditLoading(true)
      
      // Call API update customer
      await (customerService as any).updateCustomer(currentCustomer!.id, {
        fullName: values.fullName,
        phone: values.phone,
        email: values.email,
        source: values.source,
        interests: values.interests || [],
        notes: values.notes,
        tags: values.tags || [],
        isActive: values.isActive
      })
      
      enqueueSnackbar('Cập nhật khách hàng thành công!', { variant: 'success' })
      setShowEditModal(false)
      setCurrentCustomer(null)
      editForm.resetFields()
      setFilter({ ...filter }) // refetch
    } catch (err: any) {
      enqueueSnackbar('Cập nhật khách hàng thất bại!', { variant: 'error' })
      console.log('err updateCustomer', err)
    } finally {
      setEditLoading(false)
    }
  }

  const handleEditModalCancel = () => {
    setShowEditModal(false)
    setCurrentCustomer(null)
    editForm.resetFields()
  }

  const handleDelete = async (customerId: string) => {
    try {
      await (customerService as any).deleteCustomer(customerId)
      enqueueSnackbar('Xóa khách hàng thành công!', { variant: 'success' })
      setFilter({ ...filter }) // refetch
    } catch (err: any) {
      enqueueSnackbar('Xóa khách hàng thất bại!', { variant: 'error' })
      console.log('err deleteCustomer', err)
    }
  }

  const columns = [
    {
      title: 'Tên khách hàng',
      key: 'customerName',
      width: 200,
      render: (_: any, record: Customer) => (
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
          {record.fullName}
        </div>
      )
    },
    {
      title: 'Thông tin liên hệ',
      key: 'contactInfo',
      width: 250,
      render: (_: any, record: Customer) => (
        <div>
          <div style={{ marginBottom: '8px' }}>
            <Tag 
              color="blue" 
              style={{ cursor: 'pointer', fontSize: '13px' }}
              onClick={() => window.open(`mailto:${record.email}`)}
            >
              📧 {record.email}
            </Tag>
          </div>
          <div>
            <Tag 
              color="green" 
              style={{ cursor: 'pointer', fontSize: '13px' }}
              onClick={() => window.open(`tel:${record.phone}`)}
            >
              📞 {record.phone}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      key: 'source',
      render: (source: string) => {
        let color = 'default';
        switch (source) {
          case 'website': color = 'blue'; break;
          case 'newsletter': color = 'green'; break;
          case 'contact_form': color = 'orange'; break;
          case 'social_media': color = 'purple'; break;
          case 'referral': color = 'cyan'; break;
          case 'other': color = 'magenta'; break;
          default: color = 'default';
        }
        const labelMap: Record<string, string> = {
          website: 'Website',
          newsletter: 'Newsletter',
          contact_form: 'Form liên hệ',
          social_media: 'Mạng xã hội',
          referral: 'Giới thiệu',
          other: 'Khác'
        };
        return <Tag color={color}>{labelMap[source] || source}</Tag>;
      }
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive?: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Ngưng hoạt động'}
        </Tag>
      )
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string) => new Date(date).toLocaleString('vi-VN')
    },
    // {
    //   title: 'Số đơn',
    //   dataIndex: ['orderStats', 'orderCount'],
    //   key: 'orderCount',
    //   render: (_: any, record: Customer) => record.orderStats?.orderCount ?? 0
    // },
    // {
    //   title: 'Tổng chi',
    //   dataIndex: ['orderStats', 'totalSpent'],
    //   key: 'totalSpent',
    //   render: (_: any, record: Customer) => record.orderStats?.totalSpent?.toLocaleString('vi-VN') ?? 0
    // },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_: any, record: Customer) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            type="text"
            size="small"
          />
          <Popconfirm
            title="Xóa khách hàng"
            description="Bạn có chắc chắn muốn xóa khách hàng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              type="text"
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  const textAreaStyle = { width: '100%', borderRadius: 4, fontSize: 16, padding: '6px 12px' }

  return (
    <div className='p-6'>
      <Row justify='space-between' align='middle' className='mb-6'>
        <Col>
          <Title level={2} className='m-0'>Quản lý khách hàng</Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined spin={isLoading} />} onClick={handleRefresh} disabled={isLoading} />
        </Col>
      </Row>
      <Card className='mb-6'>
        <Row justify='space-between' align='middle'>
          <Col>
            <Space size={12} align='start'>
              <div>
                <label className='block mb-1 text-sm font-medium text-gray-700'>Tìm kiếm</label>
                <Search
                  placeholder='Tìm theo tên, email, số điện thoại'
                  allowClear
                  onSearch={handleSearch}
                  onChange={e => handleSearch(e.target.value)}
                  style={{ width: 360, maxWidth: '100%' }}
                />
              </div>
              <div>
                <label className='block mb-1 text-sm font-medium text-gray-700'>Nguồn</label>
                <Select
                  style={{ width: 180, maxWidth: '100%' }}
                  placeholder='Tất cả nguồn'
                  allowClear
                  value={filter.source}
                  onChange={handleSourceChange}
                >
                  {sourceOptions.map(opt => (
                    <Option key={opt.value ?? ''} value={opt.value ?? ''}>{opt.label}</Option>
                  ))}
                </Select>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined spin={isLoading} />} onClick={handleRefresh} disabled={isLoading} />
              <Button type='primary' icon={<PlusOutlined />} className='bg-green-600 border-green-600 hover:bg-green-700' onClick={handleOpenModal}>
                Thêm khách hàng
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      {isLoading ? (
        <div className='flex justify-center items-center h-64'>
          <Spin size='large' tip='Đang tải...' />
        </div>
      ) : customers.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            dataSource={customers.map(c => ({ ...c, key: c.id }))}
            rowKey='id'
            bordered
            pagination={false}
          />
          <div className='flex justify-end mt-4'>
            <Pagination
              current={filter.page}
              pageSize={filter.limit}
              total={total}
              onChange={handlePageChange}
              showSizeChanger
              pageSizeOptions={['10', '20', '50', '100']}
            />
          </div>
        </Card>
      ) : (
        <Card className='p-6 text-center'>
          <Empty description='Không có khách hàng nào.' image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      )}
      <Modal
        title='Tạo khách hàng mới'
        open={showModal}
        onCancel={handleCloseModal}
        onOk={handleCreateCustomer}
        okText='Tạo mới'
        cancelText='Hủy'
        confirmLoading={createLoading}
      >
        <Form layout='vertical' form={form}>
          <Form.Item 
            name='email' 
            label='Email' 
            rules={[{ required: true, type: 'email', message: 'Vui lòng nhập email hợp lệ' }]}
          >
            <Input 
              placeholder="Nhập email" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item 
            name='fullName' 
            label='Họ tên' 
            rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
          >
            <Input 
              placeholder="Nhập họ tên" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item name='phone' label='Số điện thoại'>
            <Input 
              placeholder="Nhập số điện thoại" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item 
            name='source' 
            label='Nguồn' 
            rules={[{ required: true, message: 'Vui lòng chọn nguồn' }]}
          >
            <Select>
              <Option value="website">Website</Option>
              <Option value="newsletter">Newsletter</Option>
              <Option value="contact_form">Form liên hệ</Option>
              <Option value="social_media">Mạng xã hội</Option>
              <Option value="referral">Giới thiệu</Option>
              <Option value="other">Khác</Option>
            </Select></Form.Item>
          <Form.Item name='interests' label='Quan tâm'>
            <Select mode='tags' placeholder='Nhập và enter để thêm'/> 
            </Form.Item>
          <Form.Item name='notes' label='Ghi chú'> <TextArea rows={2} style={textAreaStyle} /> </Form.Item>
          <Form.Item name='tags' label='Tags'>
            <Select mode='tags' placeholder='Nhập và enter để thêm' dropdownStyle={{ fontSize: 16 }} /> </Form.Item>
        </Form>
      </Modal>
      
      {/* Modal Edit Customer */}
      <Modal
        title='Chỉnh sửa khách hàng'
        open={showEditModal}
        onCancel={handleEditModalCancel}
        onOk={handleEditModalOk}
        okText='Lưu'
        cancelText='Hủy'
        confirmLoading={editLoading}
      >
        <Form layout='vertical' form={editForm}>
          <Form.Item 
            name='email' 
            label='Email' 
            rules={[{ required: true, type: 'email', message: 'Vui lòng nhập email hợp lệ' }]}
          >
            <Input 
              placeholder="Nhập email" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item 
            name='fullName' 
            label='Họ tên' 
            rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
          >
            <Input 
              placeholder="Nhập họ tên" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item name='phone' label='Số điện thoại'>
            <Input 
              placeholder="Nhập số điện thoại" 
              style={{ 
                width: '100%', 
                height: 32, 
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
              }}
            />
          </Form.Item>
          <Form.Item 
            name='source' 
            label='Nguồn' 
            rules={[{ required: true, message: 'Vui lòng chọn nguồn' }]}
          >
            <Select>
              <Option value="website">Website</Option>
              <Option value="newsletter">Newsletter</Option>
              <Option value="contact_form">Form liên hệ</Option>
              <Option value="social_media">Mạng xã hội</Option>
              <Option value="referral">Giới thiệu</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name='isActive'
            label='Trạng thái'
            valuePropName='checked'
            style={{ marginBottom: '16px' }}
          >
            <Switch checkedChildren="Hoạt động" unCheckedChildren="Ngưng" />
          </Form.Item>
          <Form.Item name='interests' label='Quan tâm'>
            <Select mode='tags' placeholder='Nhập và enter để thêm'/> 
          </Form.Item>
          <Form.Item name='notes' label='Ghi chú'> 
            <TextArea rows={2} style={textAreaStyle} /> 
          </Form.Item>
          <Form.Item name='tags' label='Tags'>
            <Select mode='tags' placeholder='Nhập và enter để thêm' dropdownStyle={{ fontSize: 16 }} /> 
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Customers 